import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faBuilding,
  faLayerGroup,
  faSquare,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import RepairTypeBadge from './RepairTypeBadge';
import RepairCostDetailModal from './RepairCostDetailModal';

/**
 * SimulationResultsTable Component
 * 
 * Displays simulation results in an expanded tabular format similar to Excel:
 * - Hierarchical rows (Bridge → Span → Maintenance → Activity)
 * - Years as columns
 * - All costs visible at once
 * - Expandable/collapsible sections
 */
const SimulationResultsTable = ({ 
  data, 
  expandedItems, 
  onToggleExpanded, 
  searchTerm = '',
  filterType = 'all',
  showDecayPercent = false,
  simulationResults = null // Full simulation results for detailed data
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedRepairData, setSelectedRepairData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatArea = (area) => {
    return `${parseFloat(area).toLocaleString()} m²`;
  };

  // Extract all unique years from the data
  const allYears = useMemo(() => {
    const years = new Set();
    
    const extractYears = (item) => {
      if (item.years && item.years.length > 0) {
        item.years.forEach(year => years.add(year.year));
      }
      if (item.children) {
        item.children.forEach(child => extractYears(child));
      }
    };
    
    if (data) {
      extractYears(data);
    }
    
    return Array.from(years).sort();
  }, [data]);

  // Calculate totals for each year
  const calculateYearTotals = (item, year) => {
    // Check if this item has structural children (not just maintenance/activity children)
    const hasStructuralChildren = item.children && item.children.some(child => 
      child.type !== 'maintenance' && child.type !== 'activity'
    );
    
    // If item has structural children, use only the parent's cost (which is already the sum of children)
    // Don't add children's costs again to avoid double counting
    if (hasStructuralChildren) {
      if (item.years) {
        const yearData = item.years.find(y => y.year === year);
        if (yearData) {
          return yearData.cost;
        }
      }
      return 0;
    }
    
    // For leaf nodes (no structural children), use the item's own cost
    let total = 0;
    if (item.years) {
      const yearData = item.years.find(y => y.year === year);
      if (yearData) {
        total += yearData.cost;
      }
    }
    
    // For leaf nodes, also add maintenance/activity children costs (if any)
    if (item.children) {
      item.children.forEach(child => {
        // Only add maintenance/activity children (structural children are already in parent cost)
        if (child.type === 'maintenance' || child.type === 'activity') {
          total += calculateYearTotals(child, year);
        }
      });
    }
    
    return total;
  };

  // Get degradation percent for a specific item and year
  const getYearDegradationPercent = (item, year) => {
    if (item.years) {
      const yearData = item.years.find(y => y.year === year);
      if (yearData && yearData.degradationPercent !== undefined) {
        return yearData.degradationPercent;
      }
    }
    
    // If this item doesn't have degradation data, check children
    // (for aggregated levels, we might want to show the average or max)
    if (item.children && item.children.length > 0) {
      const childDegradations = item.children
        .map(child => getYearDegradationPercent(child, year))
        .filter(val => val !== null && val !== undefined && val > 0);
      
      if (childDegradations.length > 0) {
        // Return the maximum degradation percent among children
        return Math.max(...childDegradations);
      }
    }
    
    return null;
  };

  const matchesSearch = (item, searchTerm) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    
    // Check if this item matches
    const itemMatches = (
      item.name?.toLowerCase().includes(search) ||
      item.code?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search)
    );
    
    // If the item itself matches, it's a match
    if (itemMatches) return true;
    
    // Check if any child matches (recursive)
    if (item.children && item.children.length > 0) {
      return item.children.some(child => matchesSearch(child, searchTerm));
    }
    
    return false;
  };

  const matchesFilter = (item, filterType) => {
    if (filterType === 'all') return true;
    return item.type === filterType;
  };

  const toggleRowExpansion = (rowId, event) => {
    // Prevent any scroll behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case 'bridge':
        return faBuilding;
      case 'asset':
        return faLayerGroup;
      case 'span':
        return faSquare;
      case 'panelbay':
        return faSquare;
      case 'maintenance':
        return faChartLine;
      case 'activity':
        return faChartLine;
      default:
        return faSquare;
    }
  };

  const getRowStyle = (level) => {
    switch (level) {
      case 0: return 'bg-blue-50 border-blue-200';
      case 1: return 'bg-green-50 border-green-200';
      case 2: return 'bg-yellow-50 border-yellow-200';
      case 3: return 'bg-purple-50 border-purple-200';
      case 4: return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getTextColor = (level) => {
    switch (level) {
      case 0: return 'text-blue-900';
      case 1: return 'text-green-900';
      case 2: return 'text-yellow-900';
      case 3: return 'text-purple-900';
      case 4: return 'text-gray-900';
      default: return 'text-gray-900';
    }
  };

  // Helper function to aggregate multiple span results into one
  const aggregateSpanResults = (spanResults, parentItem, year) => {
    if (!spanResults || spanResults.length === 0) return null;
    if (spanResults.length === 1) return spanResults[0];

    // Aggregate all span results
    const aggregated = {
      spanId: null, // Parent doesn't have a single span ID
      spanCode: parentItem.code || parentItem.name || 'Aggregated',
      spanName: parentItem.name || parentItem.code || 'Aggregated Elements',
      year: year,
      totalCost: 0,
      costA: 0,
      costB: 0,
      costC: 0,
      areaA: 0,
      areaB: 0,
      areaC: 0,
      degradationPercent: 0,
      repairProcessesA: [],
      repairProcessesB: [],
      repairProcessesC: [],
      repairType: null,
      corrosionCategory: null,
      yearsSinceRepair: 0,
      repairTriggered: false
    };

    // Track unique processes by name
    const processesMapA = new Map();
    const processesMapB = new Map();
    const processesMapC = new Map();
    let totalDegradation = 0;
    let countWithDegradation = 0;

    spanResults.forEach(sr => {
      // Sum costs
      aggregated.totalCost += sr.totalCost || 0;
      aggregated.costA += sr.costA || 0;
      aggregated.costB += sr.costB || 0;
      aggregated.costC += sr.costC || 0;
      
      // Sum areas
      aggregated.areaA += sr.areaA || 0;
      aggregated.areaB += sr.areaB || 0;
      aggregated.areaC += sr.areaC || 0;

      // Track degradation (average)
      if (sr.degradationPercent !== undefined && sr.degradationPercent !== null) {
        totalDegradation += sr.degradationPercent;
        countWithDegradation++;
      }

      // Merge unique processes
      [...(sr.repairProcessesA || [])].forEach(p => {
        const name = p.processName || p.name || 'Unknown';
        if (!processesMapA.has(name)) {
          processesMapA.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999
          });
        }
      });

      [...(sr.repairProcessesB || [])].forEach(p => {
        const name = p.processName || p.name || 'Unknown';
        if (!processesMapB.has(name)) {
          processesMapB.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999
          });
        }
      });

      [...(sr.repairProcessesC || [])].forEach(p => {
        const name = p.processName || p.name || 'Unknown';
        if (!processesMapC.has(name)) {
          processesMapC.set(name, {
            processName: name,
            name: name,
            sequenceOrder: p.sequenceOrder || 999
          });
        }
      });

      // Use first non-null repair type
      if (!aggregated.repairType && sr.repairType) {
        aggregated.repairType = sr.repairType;
      }

      // Use first non-null corrosion category
      if (!aggregated.corrosionCategory && sr.corrosionCategory) {
        aggregated.corrosionCategory = sr.corrosionCategory;
      }

      // Repair triggered if any child has it
      if (sr.repairTriggered) {
        aggregated.repairTriggered = true;
      }
    });

    // Convert process maps to arrays and sort by sequence order
    aggregated.repairProcessesA = Array.from(processesMapA.values())
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    aggregated.repairProcessesB = Array.from(processesMapB.values())
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    aggregated.repairProcessesC = Array.from(processesMapC.values())
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder);

    // Calculate average degradation
    if (countWithDegradation > 0) {
      aggregated.degradationPercent = totalDegradation / countWithDegradation;
    }

    return aggregated;
  };

  // Helper function to find span result data for a given item and year
  const findSpanResult = (item, year) => {
    if (!simulationResults || !simulationResults.yearlyResults) return null;
    
    // Table year is calendar year; yearlyResults uses simulation year (1, 2, 3...)
    // Use simulation start year (Default Year 1 from settings) or current year
    const startYear = simulationResults.simulationStartYear != null && simulationResults.simulationStartYear > 0
      ? simulationResults.simulationStartYear
      : new Date().getFullYear();
    const simulationYear = year - startYear + 1;
    
    if (simulationYear < 1 || simulationYear > (simulationResults.timeHorizonYears || 30)) {
      return null;
    }
    
    // Find the year result by simulation year
    const yearResult = simulationResults.yearlyResults.find(yr => yr.year === simulationYear);
    if (!yearResult || !yearResult.spanResults) return null;

    // Strategy 1: Direct ID match (item.id should match spanId for span-level nodes)
    // This is the most reliable method for span-level nodes
    if (item.id) {
      const spanResult = yearResult.spanResults.find(sr => sr.spanId === item.id);
      if (spanResult && spanResult.totalCost > 0) return spanResult;
    }

    // Strategy 2: For activity/maintenance nodes, we need to find the parent span
    // Since we don't have parent reference, try to find by matching the first span with cost
    // that matches the item's code pattern or find any span with cost in this year
    if (item.type === 'activity' || item.type === 'maintenance') {
      // For activity nodes, try to find the parent span by looking for spans with matching codes
      // The hierarchy structure shows activity nodes are children of span nodes
      // We'll try to find any span result that has cost > 0 in this year
      const spanWithCost = yearResult.spanResults.find(sr => sr.totalCost > 0);
      if (spanWithCost) return spanWithCost;
    }

    // Strategy 3: Try to find by code/name match (exact match only for leaf nodes)
    // Only use this for nodes that are likely leaf nodes (no children or only maintenance children)
    const isLeafNode = !item.children || item.children.length === 0 || 
                      item.children.every(child => child.type === 'maintenance' || child.type === 'activity');
    
    if (isLeafNode && (item.code || item.name)) {
      const searchCode = item.code || item.name;
      // Try exact match first
      let spanResult = yearResult.spanResults.find(sr => {
        const codeMatch = sr.spanCode === searchCode || sr.spanCode === searchCode.toString();
        const nameMatch = sr.spanName === searchCode || sr.spanName === item.name;
        return (codeMatch || nameMatch) && sr.totalCost > 0;
      });
      if (spanResult) return spanResult;
      
      // For leaf nodes, try code starts with match (but only if it's a direct match, not deep hierarchy)
      spanResult = yearResult.spanResults.find(sr => {
        if (!sr.spanCode) return false;
        // Only match if codes are very similar (exact or one level difference)
        const codeStartsWith = sr.spanCode.startsWith(searchCode);
        if (!codeStartsWith) return false;
        
        // Check if the remaining part is short (suggests direct relationship)
        const remaining = sr.spanCode.substring(searchCode.length);
        // Allow if remaining is empty, or starts with separator and has short remainder
        return remaining.length === 0 || 
               (remaining.startsWith('-') && remaining.split('-').length <= 2);
      });
      if (spanResult && spanResult.totalCost > 0) return spanResult;
    }

    // Strategy 4: For aggregated rows (bridge, plinth, pillar, etc.), find spans that belong to this parent
    // Traverse children to find actual span nodes and match their span results
    if (item.type === 'bridge' || (item.children && item.children.length > 0)) {
      // Helper function to recursively find all span IDs in the subtree (leaf nodes with IDs)
      const findSpanIdsInSubtree = (node) => {
        const spanIds = [];
        
        // If this node has an ID and no children (or only maintenance/activity children), it's a leaf span node
        const hasStructuralChildren = node.children && node.children.some(child => 
          child.type !== 'maintenance' && child.type !== 'activity'
        );
        
        if (node.id && !hasStructuralChildren) {
          // This is a leaf node (actual span), add its ID
          spanIds.push(node.id);
        }
        
        // Recursively check structural children (skip maintenance/activity nodes)
        if (node.children && node.children.length > 0) {
          node.children.forEach(child => {
            // Only traverse structural children (not maintenance/activity)
            if (child.type !== 'maintenance' && child.type !== 'activity') {
              const childSpanIds = findSpanIdsInSubtree(child);
              spanIds.push(...childSpanIds);
            }
          });
        }
        
        return spanIds;
      };
      
      // Find all span IDs that belong to this parent's subtree
      const spanIdsInSubtree = findSpanIdsInSubtree(item);
      
      // Find span results that match any of these span IDs
      if (spanIdsInSubtree.length > 0) {
        const matchingSpanResults = yearResult.spanResults.filter(sr => 
          spanIdsInSubtree.includes(sr.spanId) && sr.totalCost > 0
        );
        
        if (matchingSpanResults.length > 0) {
          // Helper to find direct children span IDs (not recursive)
          const findDirectChildSpanIds = (parentItem) => {
            if (!parentItem.children) return [];
            return parentItem.children
              .filter(child => child.type !== 'maintenance' && child.type !== 'activity')
              .map(child => {
                // If child has no structural children, it's a leaf span
                const hasStructuralChildren = child.children && child.children.some(c => 
                  c.type !== 'maintenance' && c.type !== 'activity'
                );
                if (child.id && !hasStructuralChildren) {
                  return child.id;
                }
                return null;
              })
              .filter(id => id !== null);
          };
          
          // First, try to find span results from direct children only
          const directChildSpanIds = findDirectChildSpanIds(item);
          if (directChildSpanIds.length > 0) {
            const directChildResults = matchingSpanResults.filter(sr => 
              directChildSpanIds.includes(sr.spanId)
            );
            if (directChildResults.length > 0) {
              // For parent nodes, aggregate all children's span results
              // This shows combined data from all contributing children
              return aggregateSpanResults(directChildResults, item, year);
            }
          }
          
          // If no direct children found, try to find spans that match the parent's code pattern
          // This helps when parent code is like "PLINTH-05" and we want spans like "PLINTH-05-PIL-04-..."
          if (item.code || item.name) {
            const parentCode = item.code || item.name;
            // Find spans whose code starts with parent code followed by a separator (dash, underscore, etc.)
            const patternMatchResults = matchingSpanResults.filter(sr => {
              if (!sr.spanCode) return false;
              // Check if span code starts with parent code followed by a separator
              const startsWithParent = sr.spanCode.startsWith(parentCode);
              if (!startsWithParent) return false;
              
              // Check if there's a separator after the parent code (not just a longer code)
              const remaining = sr.spanCode.substring(parentCode.length);
              return remaining.startsWith('-') || remaining.startsWith('_') || remaining.length === 0;
            });
            
            if (patternMatchResults.length > 0) {
              // Aggregate all matching results
              return aggregateSpanResults(patternMatchResults, item, year);
            }
          }
          
          // Fallback: If no pattern match, aggregate all matching results (deep descendants)
          return aggregateSpanResults(matchingSpanResults, item, year);
        }
      }
      
      // Fallback: if no match found in subtree, try to find by code/name pattern
      // This handles cases where the hierarchy structure might not perfectly match
      if (item.code || item.name) {
        const codePattern = item.code || item.name;
        const matchingSpanResults = yearResult.spanResults.filter(sr => {
          // More specific matching: code should start with pattern or pattern should start with code
          const codeStartsWith = codePattern && sr.spanCode && (
            sr.spanCode.startsWith(codePattern) || codePattern.startsWith(sr.spanCode)
          );
          const nameIncludes = item.name && sr.spanName && (
            sr.spanName.includes(item.name) || item.name.includes(sr.spanName)
          );
          return (codeStartsWith || nameIncludes) && sr.totalCost > 0;
        });
        
        if (matchingSpanResults.length > 0) {
          // Return the one with highest cost
          return matchingSpanResults.reduce((max, current) => 
            current.totalCost > max.totalCost ? current : max
          );
        }
      }
      
      // Last fallback for parent nodes: return first span with cost (should rarely happen)
      const spanWithCost = yearResult.spanResults.find(sr => sr.totalCost > 0);
      if (spanWithCost) return spanWithCost;
    }

    // Strategy 5: Last resort - find any span result with cost > 0 for this year
    // This ensures we can show some data even if exact match fails
    const anySpanWithCost = yearResult.spanResults.find(sr => sr.totalCost > 0);
    return anySpanWithCost || null;
  };

  // Helper function to determine repair type from span result (use backend label so table and modal match)
  const getRepairTypeFromResult = (spanResult) => {
    if (!spanResult) return null;

    // Prefer repairLabel from new simulation engine (TU, OC, RE)
    const label = (spanResult.repairLabel || '').toUpperCase();
    if (label === 'TU') return 'touchup';
    if (label === 'OC') return 'overcoat';
    if (label === 'RE') return 'recoat';

    // Use repairType from backend if available
    if (spanResult.repairType) {
      return spanResult.repairType;
    }

    // Fallback: determine from processes
    const allProcesses = [
      ...(spanResult.repairProcessesA || []),
      ...(spanResult.repairProcessesB || []),
      ...(spanResult.repairProcessesC || [])
    ].map(p => (p.processName || p.name || '').toLowerCase());

    const hasWash = allProcesses.some(p => p.includes('wash'));
    const hasPrime = allProcesses.some(p => p.includes('prime'));
    const hasStripe = allProcesses.some(p => p.includes('stripe'));
    const hasMid = allProcesses.some(p => p.includes('mid'));
    const hasTop = allProcesses.some(p => p.includes('top'));

    // Only wash = Wash
    if (hasWash && !hasPrime && !hasStripe && !hasMid && !hasTop) {
      return 'wash';
    }
    // Only topcoat = Touch-up
    if (!hasPrime && !hasStripe && !hasMid && hasTop) {
      return 'touchup';
    }
    // Multiple processes = Recoat/Full
    if (hasPrime || hasStripe || hasMid) {
      return 'recoat';
    }

    return null;
  };

  // Handle cell click
  const handleCellClick = (item, year, yearCost) => {
    if (yearCost <= 0) return; // Don't open modal for zero costs
    
    const spanResult = findSpanResult(item, year);
    if (spanResult) {
      setSelectedRepairData({
        ...spanResult,
        year: year,
        spanCode: spanResult.spanCode || item.code,
        spanName: spanResult.spanName || item.name,
        spanId: spanResult.spanId || item.id
      });
      setIsModalOpen(true);
    }
  };

  const TableRow = ({ item, level = 0, rowId, parentExpanded = true }) => {
    const isExpanded = expandedRows.has(rowId);
    const hasChildren = item.children && item.children.length > 0;
    const matchesSearchTerm = matchesSearch(item, searchTerm);
    const matchesFilterType = matchesFilter(item, filterType);

    // Don't render if it doesn't match filters or parent is collapsed
    if (!matchesSearchTerm || !matchesFilterType || !parentExpanded) {
      return null;
    }

    const totalCost = allYears.reduce((sum, year) => sum + calculateYearTotals(item, year), 0);

    return (
      <>
        {/* Main Row */}
        <tr className={`${getRowStyle(level)} hover:bg-opacity-80 transition-colors`}>
          {/* Expand/Collapse Column */}
          <td className="px-3 py-2 border-r border-gray-200 sticky left-0 bg-inherit z-10">
            {hasChildren && (
              <button
                onClick={(e) => toggleRowExpansion(rowId, e)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                type="button"
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  className="w-4 h-4"
                />
              </button>
            )}
          </td>

          {/* Item Details Column */}
          <td className="px-3 py-2 border-r border-gray-200 sticky left-8 bg-inherit z-10">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon
                icon={getIcon(item.type)}
                className={`w-4 h-4 ${getTextColor(level)}`}
              />
              <div>
                <div className={`font-medium ${getTextColor(level)}`}>
                  {item.name || item.code || 'Unnamed Item'}
                </div>
                {item.description && (
                  <div className="text-xs text-gray-600">
                    {item.description}
                  </div>
                )}
                {item.area && (
                  <div className="text-xs text-gray-500">
                    Area: {formatArea(item.area)}
                  </div>
                )}
              </div>
            </div>
          </td>

          {/* Year Columns */}
          {allYears.map(year => {
            const yearCost = calculateYearTotals(item, year);
            const degradationPercent = showDecayPercent ? getYearDegradationPercent(item, year) : null;
            // Only show decay percent if there's a cost (repair was triggered) or if it's meaningful
            const shouldShowDecay = showDecayPercent && 
              degradationPercent !== null && 
              degradationPercent > 0 && 
              (yearCost > 0 || degradationPercent >= 1.0); // Show if cost > 0 OR decay >= 1%
            
            // Find span result for this cell - try to find for any row with cost > 0
            const spanResult = yearCost > 0 ? findSpanResult(item, year) : null;
            const repairType = spanResult ? getRepairTypeFromResult(spanResult) : null;
            // Make clickable if there's a cost and we found span result data
            const isClickable = yearCost > 0 && spanResult !== null;
            
            return (
              <td 
                key={year} 
                className={`px-3 py-2 text-right border-r border-gray-200 ${isClickable ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                onClick={() => isClickable && handleCellClick(item, year, yearCost)}
                title={isClickable ? 'Click to view detailed cost breakdown' : yearCost > 0 ? 'No detailed data available' : ''}
              >
                <div className="flex flex-col items-end gap-1">
                  {yearCost > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-medium ${isClickable ? 'text-blue-700 underline' : 'text-gray-900'}`}>
                        {formatCurrency(yearCost)}
                      </span>
                      {repairType && (
                        <RepairTypeBadge repairType={repairType} />
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                  {shouldShowDecay && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      {degradationPercent.toFixed(2)}%
                    </span>
                  )}
                </div>
              </td>
            );
          })}

          {/* Total Column */}
          <td className="px-3 py-2 text-right bg-gray-100">
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(totalCost)}
            </span>
          </td>
        </tr>

        {/* Child Rows */}
        {isExpanded && hasChildren && item.children.map((child, index) => (
          <TableRow
            key={`${rowId}-${index}`}
            item={child}
            level={level + 1}
            rowId={`${rowId}-${index}`}
            parentExpanded={isExpanded}
          />
        ))}
      </>
    );
  };

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        No simulation data available
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto" style={{ scrollBehavior: 'auto' }}>
        <table className="min-w-full border border-gray-200 rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-8 sticky left-0 bg-gray-100 z-10">
                {/* Expand/Collapse column */}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-64 sticky left-8 bg-gray-100 z-10">
                Item
              </th>
              {allYears.map(year => (
                <th key={year} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-24">
                  {year}
                </th>
              ))}
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200 min-w-24">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <TableRow
              item={data}
              level={0}
              rowId="root"
              parentExpanded={true}
            />
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td className="px-3 py-3 border-r border-gray-200 sticky left-0 bg-gray-100 z-10"></td>
              <td className="px-3 py-3 font-semibold text-gray-900 border-r border-gray-200 sticky left-8 bg-gray-100 z-10">
                Grand Total
              </td>
              {allYears.map(year => {
                const yearTotal = calculateYearTotals(data, year);
                return (
                  <td key={year} className="px-3 py-3 text-right font-semibold text-gray-900 border-r border-gray-200">
                    {formatCurrency(yearTotal)}
                  </td>
                );
              })}
              <td className="px-3 py-3 text-right font-semibold text-gray-900 bg-gray-200">
                {formatCurrency(allYears.reduce((sum, year) => sum + calculateYearTotals(data, year), 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* Repair Cost Detail Modal */}
      <RepairCostDetailModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRepairData(null);
        }}
        repairData={selectedRepairData}
      />
    </>
  );
};

export default SimulationResultsTable;
